import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ResultRendererProps } from './types';

type Row = {
  base_url: string;
  directory: string;
  full_url: string;
  sensitive: boolean;
};

function decodeHtml(s: string): string {
  return s
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');
}

function normalize(results: any[]): Row[] {
  if (!Array.isArray(results)) return [];
  const out: Row[] = [];
  const seen = new Set<string>();

  for (const it of results) {
    const base_url  = decodeHtml(String(it?.base_url ?? ''));
    const directory = decodeHtml(String(it?.directory ?? ''));
    const full_url  = decodeHtml(String(it?.full_url ?? ''));
    const sensitive = Boolean(it?.sensitive);

    if (!full_url) continue;
    const key = `${full_url}::${sensitive}`;
    if (seen.has(key)) continue;
    seen.add(key);

    out.push({ base_url, directory, full_url, sensitive });
  }

  // Sensibles primero, luego por URL
  out.sort((a, b) => {
    if (a.sensitive !== b.sensitive) return a.sensitive ? -1 : 1;
    return a.full_url.localeCompare(b.full_url);
  });
  return out;
}

const PAGE_SIZE = 25;

const GauResults: React.FC<ResultRendererProps> = ({ results }) => {
  const { t } = useTranslation();
  const rows = React.useMemo(() => normalize(results), [results]);

  const [page, setPage] = React.useState(1);
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  React.useEffect(() => {
    // si cambian los resultados, resetea a página 1
    setPage(1);
  }, [results]);

  if (rows.length === 0) {
    return <p className="text-sm text-slate-500">{t('tools.results_scan.gau.empty')}</p>;
  }

  const sensitiveCount = rows.filter(r => r.sensitive).length;

  return (
    <div className="w-full space-y-4">
      {/* Resumen */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-slate-700">
          <span className="font-semibold text-slate-900">{t('tools.results_scan.gau.title')}</span>
          <span className="mx-1 text-slate-400">•</span>
          <span>{t('tools.results_scan.gau.found_count', { count: rows.length })}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 bg-rose-50 text-rose-700 ring-rose-200">
            {t('tools.results_scan.gau.sensitive', 'Sensitive')}: {sensitiveCount}
          </span>
          <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 bg-slate-900 text-white ring-black/10">
            {rows.length}
          </span>
        </div>
      </div>

      {/* Tabla */}
      <div className="rounded-lg border border-slate-200 overflow-auto bg-white">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-gradient-to-r from-sky-50 to-indigo-50">
            <tr className="text-slate-700">
              <th className="px-3 py-2 text-left uppercase tracking-wide text-[11px]">
                {t('tools.results_scan.gau.full_url')}
              </th>
              <th className="px-3 py-2 text-left uppercase tracking-wide text-[11px]">
                {t('tools.results_scan.gau.directory')}
              </th>
              <th className="px-3 py-2 text-center uppercase tracking-wide text-[11px]">
                {t('tools.results_scan.gau.sensitive')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {pageRows.map((r, i) => (
              <tr key={`${r.full_url}-${i}`} className={r.sensitive ? 'bg-rose-50/40' : (i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50')}>
                <td className="px-3 py-2 text-slate-900 break-words">
                  <a href={r.full_url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                    {r.full_url}
                  </a>
                </td>
                <td className="px-3 py-2 text-slate-700 break-words">{r.directory || '—'}</td>
                <td className="px-3 py-2 text-center">
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ${
                    r.sensitive
                      ? 'bg-rose-50 text-rose-700 ring-rose-200'
                      : 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                  }`}>
                    {r.sensitive ? t('tools.results_scan.yes') : t('tools.results_scan.pagination.no')}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 text-xs rounded ring-1 ring-slate-300 disabled:opacity-50"
          >
            {t('tools.results_scan.pagination.prev')}
          </button>
          <div className="text-xs text-slate-600">
            {t('tools.results_scan.pagination.page_of', { page, total: totalPages }) || `Page ${page} / ${totalPages}`}
          </div>
          <button
            type="button"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-1.5 text-xs rounded ring-1 ring-slate-300 disabled:opacity-50"
          >
            {t('tools.results_scan.pagination.next')}
          </button>
        </div>
      )}
    </div>
  );
};

export default GauResults;
